import { useState, useEffect } from 'react';
import Modal from './Modal';
import {
  Mail,
  Phone,
  Building,
  MapPin,
  Calendar,
  Truck,
  FileText,
  Hash,
} from 'lucide-react';
import { getAuthHeaders } from '@/utils/getauthheaders';


const ClientDetailsModal = ({ isOpen, onClose, client }) => {
  const [clientStats, setClientStats] = useState({
    transports: 0,
    totalValue: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(false);

  // Fetch client statistics and recent activity when modal opens
  useEffect(() => {
    if (isOpen && client) {
      fetchClientStats();
    }
  }, [isOpen, client]);

  const fetchClientStats = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      // Fetch client statistics using the dedicated stats endpoint
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/shipments/stats/${client.id}`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const { totalTransports, totalValue, recentShipments } = data.stats;

          // Transform recent shipments to activity format
          const recentActivity = recentShipments.map(shipment => ({
            type: 'transport',
            description: `Transport către ${shipment.destinatie || shipment.ruta_completa || '...'}`,
            date: shipment.ora_plecarii,
            status: shipment.status,
            clientLocation: shipment.client_location  // aici folosești exact câmpul din API
          }));

          setClientStats({
              transports: totalTransports,
              totalValue: totalValue,
              recentActivity
            });
        }
      }
    } catch (error) {
      console.error('Error fetching client stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Necunoscut';
    const date = new Date(dateString);
    return date.toLocaleDateString('ro-RO');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'livrat': return 'text-green-600';
      case 'pe_drum': return 'text-blue-600';
      case 'in_incarcare': return 'text-orange-600';
      case 'in_asteptare': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'livrat': return 'Livrat';
      case 'pe_drum': return 'Pe drum';
      case 'in_incarcare': return 'În încărcare';
      case 'in_asteptare': return 'În așteptare';
      default: return 'Necunoscut';
    }
  };

  if (!client) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalii Client"
    >
      <div className="space-y-6">
        {/* Client Info */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">{client.nume_firma}</h3>
            <div className="flex space-x-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">
                  {loading ? '...' : clientStats.transports}
                </p>
                <p className="text-xs text-gray-600">Transporturi</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {loading ? '...' : `${(clientStats.totalValue / 1000).toFixed(0)}K`}
                </p>
                <p className="text-xs text-gray-600">RON Total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            {client.email && (
              <div className="flex items-center text-gray-700">
                <Mail className="w-4 h-4 mr-3 text-orange-500" />
                <span className="text-sm">{client.email}</span>
              </div>
            )}
            {client.telefon && (
              <div className="flex items-center text-gray-700">
                <Phone className="w-4 h-4 mr-3 text-orange-500" />
                <span className="text-sm">{client.telefon}</span>
              </div>
            )}
            {client.cui && (
              <div className="flex items-center text-gray-700">
                <Hash className="w-4 h-4 mr-3 text-orange-500" />
                <span className="text-sm">CUI: {client.cui}</span>
              </div>
            )}
          </div>
          <div className="space-y-3">
            {client.adresa && (
              <div className="flex items-center text-gray-700">
                <MapPin className="w-4 h-4 mr-3 text-orange-500" />
                <span className="text-sm">{client.adresa}</span>
              </div>
            )}
            {client.oras && (
              <div className="flex items-center text-gray-700">
                <Building className="w-4 h-4 mr-3 text-orange-500" />
                <span className="text-sm">{client.oras}</span>
              </div>
            )}
            <div className="flex items-center text-gray-700">
              <Calendar className="w-4 h-4 mr-3 text-orange-500" />
              <span className="text-sm">
                Client din {client.created_at ? new Date(client.created_at).getFullYear() : '2024'}
              </span>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-semibold text-gray-800 mb-3">Activitate Recentă</h4>
          <div className="space-y-2">
            {loading ? (
              <div className="text-center text-gray-500 py-4">Se încarcă activitatea...</div>
            ) : clientStats.recentActivity.length > 0 ? (
              clientStats.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <Truck className="w-4 h-4 mr-2 text-blue-500" />
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-700">{activity.description}</span>
                      <span className={`text-xs ${getStatusColor(activity.status)}`}>
                        {getStatusText(activity.status)}
                      </span>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">
                    {formatDate(activity.date)}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                Nu există activitate recentă
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {client.notes && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
              <FileText className="w-4 h-4 mr-2 text-orange-500" />
              Notițe
            </h4>
            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{client.notes}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ClientDetailsModal;